// Inspector — the VW Context Rail content (mounts into AppShellLayout's rightSlot).
// Composes the @perlab/ui RightInspector primitive (3-state, edge-rail, ⌘\) with the rail modules.
// Connections (DF-4/DF-12) and AI Context (DF-4 grounding). PerLab IP lives in project Data Intelligence. Source Hub was
// retired into the full Data page (/data) and Outside-In into Intel (/intel); the rail stays a glance.
import { type ReactElement, type ReactNode } from "react";
import { clsx } from "clsx";
import { FileText, GitFork, PanelRight, Quote } from "lucide-react";
import { RightInspector, useRightInspector } from "@perlab/ui";
import { ConnectionsModule } from "./ConnectionsModule";
import { AiContextModule } from "./AiContextModule";
import { ProjectContextModule } from "./ProjectContextModule";

function RailIcon({ icon, label, sectionId }: { icon: ReactNode; label: string; sectionId: string }): ReactElement {
  const { scrollToSection, focusedSectionId } = useRightInspector();
  const active = focusedSectionId === sectionId;
  // Active (currently-focused) section icon gets the 2px FLUSH orange accent + grey-15 fill — same active
  // rule as the left rail. Default grey-55→grey-50; hover white/5 + #fff. Tooltip shows the section label.
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={() => scrollToSection(sectionId)}
      className={clsx(
        "flex h-[34px] w-[34px] items-center justify-center border-l-2 transition-colors",
        active
          ? "border-[var(--color-accent)] bg-[var(--color-neutral-grey-15)] text-white"
          : "border-transparent text-[var(--color-neutral-grey-50)] hover:bg-white/5 hover:text-white",
      )}
    >
      {icon}
    </button>
  );
}

function InspectorCollapseItem(): ReactElement {
  const { mode, cycle } = useRightInspector();
  const label = mode === "expanded" ? "Collapse" : mode === "rail" ? "Hide" : "Expand";
  // In the 48px rail the text label "{label} (⌘\)" can't fit → it clipped and bled off the bottom edge
  // (audit-03 IR-M1). Rail mode renders the control ICON-ONLY (centered), keeping the shortcut on the
  // aria-label + tooltip so it stays discoverable — mirrors the rail's icon-only section buttons above.
  const isRail = mode === "rail";
  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={`${label} (⌘\\)`}
      title={`${label} (⌘\\)`}
      className={clsx(
        "flex items-center rounded-[2px] text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text-primary)]",
        isRail ? "w-full justify-center py-2" : "w-full gap-3 px-3 py-2",
      )}
    >
      <PanelRight size={16} aria-hidden />
      {!isRail && <span className="flex-1 text-left">{label} (⌘\)</span>}
    </button>
  );
}

export function Inspector({ projectContext = false, projectOnly = false }: {
  projectContext?: boolean; projectOnly?: boolean;
}): ReactElement {
  const railIcons = (
    <>
      {projectContext && <RailIcon icon={<FileText size={16} aria-hidden />} label="Project context" sectionId="project-context" />}
      {!projectOnly && <><RailIcon icon={<GitFork size={16} aria-hidden />} label="Connections" sectionId="connections" />
        <RailIcon icon={<Quote size={16} aria-hidden />} label="AI Context" sectionId="ai-context" /></>}
    </>
  );

  return (
    <RightInspector railIcons={railIcons} footer={<InspectorCollapseItem />}>
      {projectContext && <ProjectContextModule />}
      {!projectOnly && <><ConnectionsModule /><AiContextModule /></>}
    </RightInspector>
  );
}
